import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, ChevronRight, TrendingUp, TrendingDown, Minus } from "lucide-react";

interface GroupBalanceCardProps {
  groupId: string;
  groupName: string;
  balance: number;
  memberCount?: number;
}

export function GroupBalanceCard({
  groupId,
  groupName,
  balance,
  memberCount,
}: GroupBalanceCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formatAmount = (amount: number) => {
    const formatted = new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(Math.abs(amount));
    return amount >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const isPositive = balance > 0;
  const isNegative = balance < 0;

  return (
    <div
      className={`cursor-pointer overflow-hidden rounded-xl border transition-all hover:shadow-lg active:scale-[0.98] ${
        isPositive
          ? "border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 dark:border-green-800 dark:from-green-950/40 dark:to-emerald-950/40"
          : isNegative
            ? "border-red-200 bg-gradient-to-r from-red-50 to-orange-50 dark:border-red-800 dark:from-red-950/40 dark:to-orange-950/40"
            : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50"
      }`}
      onClick={() => navigate(`/groups/${groupId}`)}
    >
      <div className="p-4">
        {/* 1行目: グループ名 */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold leading-snug text-gray-900 dark:text-white">{groupName}</h3>
          <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        </div>

        {/* 2行目: メンバー数 + 金額 */}
        <div className="mt-2 flex items-center justify-between">
          {memberCount !== undefined && (
            <p className="flex items-center gap-1 text-sm text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              {memberCount}
              {t("groups.memberCount", "メンバー")}
            </p>
          )}
          <div className="flex items-center gap-1">
            {isPositive ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : isNegative ? (
              <TrendingDown className="h-4 w-4 text-red-500" />
            ) : (
              <Minus className="h-4 w-4 text-gray-400" />
            )}
            <span
              className={`text-lg font-bold tabular-nums ${
                isPositive
                  ? "text-green-600 dark:text-green-400"
                  : isNegative
                    ? "text-red-600 dark:text-red-400"
                    : "text-gray-500"
              }`}
            >
              {formatAmount(balance)}
            </span>
            <span className="ml-1 text-xs text-muted-foreground">
              {isPositive
                ? t("home.toReceive", "受け取る")
                : isNegative
                  ? t("home.toPay", "支払う")
                  : t("home.even", "±0")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
